import i18n from '~/i18n';

import { StrategyChangeStatus, StrategyStatus } from '~/api-services/AIStrategyBuilderService/types';
import { ExchangeRates } from '~/api-services/ExchangeService/types';
import { Theme } from '~/theme';

export const STRATEGY_PERIODS = ['1h', '4h', '1d'] as const;
export type StrategyPeriod = (typeof STRATEGY_PERIODS)[number];

// Preselected when a strategy is created from scratch. Instrument codes are otherwise purely
// runtime data (the streamed symbols map keys); this one matches the strategy backend's own
// spelling for gold.
export const DEFAULT_STRATEGY_INSTRUMENT = 'XAUUSD';

export function periodLabel(code: string): string {
    return i18n.t(`period-${code}`);
}

// Only tokens that hold the same hue in both themes, so a status cannot change meaning with it.
export function statusColor(theme: Theme, status: StrategyChangeStatus): string {
    switch (status) {
        case 'enabled':
            return theme.success;
        case 'failed':
            return theme.alert;
        case 'inprogress':
        case 'done':
            return theme.strategyProgress;
        case 'seen':
        case 'unarchived':
            return theme.button;
        default:
            return theme.secondary;
    }
}

export function executionColor(theme: Theme, code: string): string {
    switch (code) {
        case 'POSITION_OPENED':
            return theme.success;
        case 'OPEN_POSITION_FAILED':
            return theme.alert;
        default:
            return theme.secondary;
    }
}

// Translated display label for a strategy status ("strategy-status-<status>" keys; locales
// without a translation fall back to English).
export function statusLabel(status: StrategyChangeStatus): string {
    return i18n.t(`strategy-status-${status}`);
}

// i18n.t echoes the key back when untranslated — that is the signal to use the server's label.
function labelOrServer(key: string, serverLabel?: string): string {
    const translated = i18n.t(key);
    return translated && translated !== key ? translated : serverLabel || '';
}

export function statusChangeLabel(status: StrategyChangeStatus, serverLabel?: string): string {
    return labelOrServer(`strategy-status-${status}`, serverLabel) || status;
}

const EXECUTION_FAILURE_REASONS: Record<string, string> = {
    InsufficientFundsMaxBuy: 'insufficient-funds',
    InsufficientFundsMaxSell: 'insufficient-funds',
    MarketClosedAtWeekend: 'market-closed',
    MarketStillNotOpen: 'market-closed',
    NoPriceException: 'no-price',
    StopLossPriceTooClose: 'stop-loss-too-close',
    StopLossPriceTooFar: 'stop-loss-too-far',
    StopLossMustBeAboveCurrentPrice: 'stop-loss-wrong-side',
    StopLossMustBeBelowCurrentPrice: 'stop-loss-wrong-side',
    TakeProfitPriceTooClose: 'take-profit-too-close',
    TakeProfitPriceTooFar: 'take-profit-too-far',
    TakeProfitMustBeAboveCurrentPrice: 'take-profit-wrong-side',
    TakeProfitMustBeBelowCurrentPrice: 'take-profit-wrong-side',
    SpreadIsBiggerThanMinDistance: 'spread-too-wide',
};

export function executionFailureReason(context?: Record<string, unknown> | null): string {
    const slug = EXECUTION_FAILURE_REASONS[String(context?.errorCode ?? '')];
    if (!slug) {
        return '';
    }
    const key = `ai-strategy-exec-reason-${slug}`;
    const translated = i18n.t(key);
    return translated && translated !== key ? translated : '';
}

export function executionMessage(
    code: string,
    serverMessage?: string,
    context?: Record<string, unknown> | null
): string {
    const base = labelOrServer(`ai-strategy-exec-${code}`, serverMessage) || code;
    const reason = code === 'OPEN_POSITION_FAILED' ? executionFailureReason(context) : '';
    return reason ? `${base} — ${reason}` : base;
}

// The engine writes -99999 across every numeric result field (and "NaN" for quantity) to mean
// "no valid value / no trade" — a strategy that optimized but produced no viable result.
export const STRATEGY_NO_DATA = -99999;

// Integers as-is, otherwise round to ≤4 decimals and trim trailing zeros. Returns "—" for
// missing, non-finite ("NaN"/"-Infinity"), or the engine's no-data sentinel. Shared by the
// result views (detail tabs, decision panel) so they format numbers identically.
export const formatStat = (value: unknown): string => {
    const num = typeof value === 'number' ? value : Number(value);
    if (value === undefined || value === null || !Number.isFinite(num) || num === STRATEGY_NO_DATA) {
        return '—';
    }
    if (Number.isInteger(num)) {
        return String(num);
    }
    return String(Number(num.toFixed(4)));
};

// Per-trade profit comes out of the backtester in the instrument's quote currency
// (balance = quantity * (price_out - price_in)). Convert it to the account currency with the
// engine's profit/loss coefficients — the same ones the TP/SL estimate divides by (see
// PendingOrderComponent). A profit uses the profit rate, a loss the loss rate.
export const convertBalance = (value: number, rates: ExchangeRates): number =>
    value / (value >= 0 ? rates.profitExchangeRate : rates.lossExchangeRate);

// Usable only when both rates are present, finite and non-zero. Strategies created before this
// app froze the rate into frontend_json have none, and then callers keep the raw values.
const hasUsableRates = (rates?: ExchangeRates): rates is ExchangeRates =>
    !!rates &&
    Number.isFinite(rates.profitExchangeRate) &&
    Number.isFinite(rates.lossExchangeRate) &&
    rates.profitExchangeRate !== 0 &&
    rates.lossExchangeRate !== 0;

// Restate the per-trade profit array in account currency, index-aligned with the input (the
// no-data sentinel is passed through untouched so index alignment survives). Null when the
// frozen rates are missing, so callers keep the engine's raw quote-currency values.
export const convertBalances = (balances: number[] | undefined, rates?: ExchangeRates): number[] | null => {
    if (!hasUsableRates(rates) || !balances || balances.length === 0) {
        return null;
    }
    return balances.map((b) => (Number.isFinite(b) && b !== STRATEGY_NO_DATA ? convertBalance(b, rates) : b));
};

// A task can be enabled (activated for live trading) once it has a result the user can act on:
// straight from `done`, or after they've viewed it (`seen`).
export const isEnableable = (status: StrategyStatus) => status === 'done' || status === 'seen';
export const isDisableable = (status: StrategyStatus) => status === 'enabled';

// The user can dismiss a result that hasn't been activated yet — from `done` or `seen`.
export const isDiscardable = (status: StrategyStatus) => status === 'done' || status === 'seen';

// Archivable statuses per the lifecycle spec (docs/ai-strategy/Frontend AI Strategy Builder.pdf):
// everything except queued/running/live strategies.
export const isArchivable = (status: StrategyStatus) =>
    status === 'done' || status === 'seen' || status === 'failed' || status === 'disabled' || status === 'discarded';

// The API version this app build speaks. Stamped onto every strategy it creates and compared
// against a strategy's stored `api_version` when viewing it. Bump this when the strategy
// input/output format changes in a way that makes older/newer strategies unsafe to edit/parse.
export const CURRENT_API_VERSION = 1;

export type ApiVersionState = 'older' | 'newer' | 'current';

// Compare a strategy's stored api_version against this app build's CURRENT_API_VERSION. A missing
// value is treated as current (no banner) so the absence of the field never blocks the UI.
export const apiVersionState = (apiVersion?: number | null): ApiVersionState => {
    const v = apiVersion ?? CURRENT_API_VERSION;
    if (v < CURRENT_API_VERSION) {
        return 'older';
    }
    if (v > CURRENT_API_VERSION) {
        return 'newer';
    }
    return 'current';
};

export const API_VERSION_OLDER_MESSAGE = 'Your strategy is created with older version of the application';
export const API_VERSION_NEWER_MESSAGE = 'Your strategy is created with newer version of the application';

// Optimization results (response.optimal_strategy) only exist once a strategy has finished
// optimizing — i.e. any state past `created`/`inprogress`/`failed`. Discarded strategies
// keep their result data (the reject decision doesn't erase the backtest).
export const hasOptimalResult = (status: StrategyStatus) =>
    status === 'done' || status === 'seen' || status === 'enabled' || status === 'disabled' || status === 'discarded';

// Turn an indicator output code into a readable label, e.g. "alligatorLips" -> "Alligator Lips".
export const humanizeOutput = (output: string): string =>
    String(output ?? '')
        .replace(/[_.]/g, ' ')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/^./, (c) => c.toUpperCase());
